import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuthContext } from '../../contexts/AuthContext';
import Dropdown from './Dropdown';
import './LanguageSelector.scss';

export const LanguageSelector = ({ variant = 'dropdown', showLabel = true }) => {
  const { currentLanguage, changeLanguage, initializeWithUser, t, getAvailableLanguages, getCurrentLanguageInfo } = useLanguage();
  const { user, isAuthenticated } = useAuthContext();
  const [isOpen, setIsOpen] = useState(false);
  const availableLanguages = getAvailableLanguages();
  const currentLangInfo = getCurrentLanguageInfo();

  // Initialize language with user data when auth changes
  useEffect(() => {
    if (initializeWithUser) {
      initializeWithUser(user, isAuthenticated);
    }
  }, [user, isAuthenticated, initializeWithUser]);

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode, user, isAuthenticated);
  };

  const getLanguageFlag = (langCode) => {
    const flagMap = {
      'fr': '🇫🇷',
      'en': '🇺🇸',
      'es': '🇪🇸',
      'de': '🇩🇪',
      'it': '🇮🇹',
      'pt': '🇵🇹',
      'ru': '🇷🇺',
      'zh': '🇨🇳',
      'ja': '🇯🇵',
      'ko': '🇰🇷'
    };
    return flagMap[langCode] || '🌐';
  };

  if (variant === 'select') {
    return (
      <div className="language-selector language-selector--select">
        {showLabel && (
          <label className="language-selector__label">
            {t('settings.language.selectLanguage')}
          </label>
        )}
        <select
          className="language-selector__select"
          value={currentLanguage}
          onChange={(e) => handleLanguageChange(e.target.value)}
        >
          {availableLanguages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.nativeName}
            </option>
          ))}
        </select>
      </div>
    );
  }

  const dropdownTrigger = (
    <button
      className="language-selector__trigger"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
    >
      <span className="language-selector__current">
        <span className="language-selector__flag">
          {getLanguageFlag(currentLangInfo.code)}
        </span>
        <span className="language-selector__name">
          {currentLangInfo.nativeName}
        </span>
      </span>
      <span className={`language-selector__arrow ${isOpen ? 'language-selector__arrow--open' : ''}`}>
        ▼
      </span>
    </button>
  );

  return (
    <div className="language-selector language-selector--dropdown">
      {showLabel && (
        <label className="language-selector__label">
          {t('settings.language.selectLanguage')}
        </label>
      )}

      <Dropdown
        trigger={dropdownTrigger}
        isOpen={isOpen}
        onToggle={setIsOpen}
        className="language-selector__dropdown"
        menuClassName="language-selector__menu"
        position="bottom-left"
      >
        {availableLanguages.map((lang) => (
          <button
            key={lang.code}
            className={`language-selector__option ${
              lang.code === currentLanguage ? 'language-selector__option--active' : ''
            }`}
            onClick={() => handleLanguageChange(lang.code)}
            role="option"
            aria-selected={lang.code === currentLanguage}
          >
            <span className="language-selector__flag">
              {getLanguageFlag(lang.code)}
            </span>
            <span className="language-selector__name">
              {lang.nativeName}
            </span>
            {lang.code === currentLanguage && (
              <span className="language-selector__check">✓</span>
            )}
          </button>
        ))}
      </Dropdown>
    </div>
  );
};

export default LanguageSelector;